import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Mail, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

/**
 * Gmail Trigger Node Configuration Component
 * Allows users to configure a "New Email in Gmail" trigger
 */
const GmailTriggerConfig = ({ nodeData, onUpdate }) => {
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(nodeData?.config?.connection_id || '');
  const [filters, setFilters] = useState(nodeData?.config?.filters || {
    from: '',
    subject: '',
    label: 'INBOX',
    has_attachment: false
  });
  const [maxResults, setMaxResults] = useState(nodeData?.config?.max_results || 10);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Load Gmail connections on mount
  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/workflows/nodes/gmail/connections`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setConnections(response.data.connections);
        
        // Auto-select first connection if none selected
        if (!selectedConnection && response.data.connections.length > 0) {
          setSelectedConnection(response.data.connections[0].connection_id);
        }
      }
    } catch (error) {
      console.error('Error loading Gmail connections:', error);
      toast.error('Failed to load Gmail connections');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      // Redirect to Gmail OAuth flow
      const response = await axios.get(`${API}/oauth/google/authorize?service=gmail`, {
        withCredentials: true
      });
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      toast.error('Failed to connect Gmail');
    }
  };

  const handleTestConnection = async () => {
    if (!selectedConnection) {
      toast.error('Please select a Gmail connection');
      return;
    }

    try {
      setTesting(true);
      const response = await axios.post(
        `${API}/workflows/nodes/gmail/test-connection`,
        { connection_id: selectedConnection },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.connected) {
        setTestResult({ success: true, message: 'Connection is valid!' });
        toast.success('Gmail connection is valid');
      } else {
        setTestResult({ success: false, message: 'Connection is invalid' });
        toast.error('Gmail connection is invalid');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({ success: false, message: error.response?.data?.detail || 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleTestTrigger = async () => {
    if (!selectedConnection) {
      toast.error('Please select a Gmail connection');
      return;
    }

    try {
      setTesting(true);
      const response = await axios.post(
        `${API}/workflows/nodes/gmail/poll-emails`,
        {
          connection_id: selectedConnection,
          filters: {
            from: filters.from || undefined,
            subject: filters.subject || undefined,
            label: filters.label,
            has_attachment: filters.has_attachment ? 'true' : undefined
          },
          max_results: maxResults
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setTestResult({
          success: true,
          message: `Found ${response.data.count} email(s)`,
          emails: response.data.emails
        });
        toast.success(`Found ${response.data.count} email(s)`);
      }
    } catch (error) {
      console.error('Error testing trigger:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to poll emails'
      });
      toast.error('Failed to poll emails');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    if (!selectedConnection) {
      toast.error('Please select a Gmail connection');
      return;
    }

    const config = {
      connection_id: selectedConnection,
      filters: {
        from: filters.from || undefined,
        subject: filters.subject || undefined,
        label: filters.label,
        has_attachment: filters.has_attachment ? 'true' : undefined
      },
      max_results: maxResults
    };

    onUpdate({
      ...nodeData,
      config,
      label: `Gmail: New Email${filters.from ? ` from ${filters.from}` : ''}`
    });

    toast.success('Gmail trigger configured');
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold">Gmail Trigger: New Email</h3>
      </div>

      {/* Gmail Connection */}
      <div className="space-y-2">
        <Label>Gmail Account</Label>
        {connections.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">No Gmail account connected</p>
            <Button onClick={handleConnectGmail} className="w-full">
              <Mail className="w-4 h-4 mr-2" />
              Connect Gmail
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Select value={selectedConnection} onValueChange={setSelectedConnection}>
              <SelectTrigger>
                <SelectValue placeholder="Select Gmail account" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.connection_id} value={conn.connection_id}>
                    {conn.email || conn.connection_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestConnection}
              disabled={testing || !selectedConnection}
            >
              {testing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Email Filters */}
      <div className="space-y-3 border-t pt-4">
        <Label className="text-base font-semibold">Email Filters (Optional)</Label>
        
        <div className="space-y-2">
          <Label htmlFor="from">From Email</Label>
          <Input
            id="from"
            placeholder="sender@example.com"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">Only trigger for emails from this sender</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="subject">Subject Contains</Label>
          <Input
            id="subject"
            placeholder="Invoice"
            value={filters.subject}
            onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">Only trigger for emails with this in the subject</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="label">Gmail Label</Label>
          <Select value={filters.label} onValueChange={(value) => setFilters({ ...filters, label: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INBOX">INBOX</SelectItem>
              <SelectItem value="UNREAD">UNREAD</SelectItem>
              <SelectItem value="STARRED">STARRED</SelectItem>
              <SelectItem value="IMPORTANT">IMPORTANT</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="has-attachment">Has Attachment</Label>
            <p className="text-xs text-muted-foreground">Only trigger for emails with attachments</p>
          </div>
          <Switch
            id="has-attachment"
            checked={filters.has_attachment}
            onCheckedChange={(checked) => setFilters({ ...filters, has_attachment: checked })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="max-results">Max Emails to Check</Label>
          <Input
            id="max-results"
            type="number"
            min="1"
            max="100"
            value={maxResults}
            onChange={(e) => setMaxResults(parseInt(e.target.value) || 10)}
          />
          <p className="text-xs text-muted-foreground">Maximum number of recent emails to check</p>
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`p-3 rounded-lg border ${
          testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-2">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                {testResult.message}
              </p>
              {testResult.emails && testResult.emails.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium">Sample emails:</p>
                  {testResult.emails.slice(0, 3).map((email, idx) => (
                    <div key={idx} className="text-xs bg-white p-2 rounded border">
                      <p className="font-medium">{email.subject}</p>
                      <p className="text-muted-foreground">From: {email.from}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleTestTrigger}
          disabled={testing || !selectedConnection}
          className="flex-1"
        >
          {testing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            'Test Trigger'
          )}
        </Button>
        <Button onClick={handleSave} disabled={!selectedConnection} className="flex-1">
          Save Configuration
        </Button>
      </div>

      {/* Data Output Info */}
      <div className="bg-muted p-3 rounded-lg">
        <p className="text-sm font-medium mb-2">Available Data Fields:</p>
        <div className="flex flex-wrap gap-1">
          <Badge variant="secondary">email.from</Badge>
          <Badge variant="secondary">email.to</Badge>
          <Badge variant="secondary">email.subject</Badge>
          <Badge variant="secondary">email.body</Badge>
          <Badge variant="secondary">email.date</Badge>
          <Badge variant="secondary">email.id</Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          These fields can be used in subsequent action nodes
        </p>
      </div>
    </div>
  );
};

export default GmailTriggerConfig;
